import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLaptopCode, FaSpinner } from 'react-icons/fa';

const CompanyDetailsPage = () => {
  const { companyId } = useParams();

  // Company data (same as in CompanyPlacementPage; consider moving to a shared file)
  const companies = {
    serviceBased: [
      {
        id: 'tcs',
        name: 'TCS',
        description: 'India’s largest IT services company, known for digital transformation solutions.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Round'],
        syllabus: ['Data Structures', 'OOPs', 'DBMS', 'Operating Systems'],
        resources: {
          coding: 'https://www.codechef.com/practice/tcs-nqt-questions',
          mcqs: 'https://www.indiabix.com/placement-papers/tcs/',
          pdf: 'https://prepinsta.com/tcs-nqt/how-to-crack-tcs-nqt/',
        },
      },
      {
        id: 'infosys',
        name: 'Infosys',
        description: 'Global leader in next-gen digital services and consulting.',
        interviewProcess: ['Online Test', 'Technical + HR Interview'],
        syllabus: ['Puzzles', 'Networking Basics', 'DBMS'],
        resources: {
          coding: 'https://www.geeksforgeeks.org/geeksforgeeks-practice-best-online-coding-platform/',
          mcqs: 'https://www.indiabix.com/placement-papers/infosys/',
          pdf: 'https://www.geeksforgeeks.org/prepare-infosys-complete-guide/',
        },
      },
      {
        id: 'wipro',
        name: 'Wipro',
        description: 'Indian multinational IT consulting and business process outsourcing company.',
        interviewProcess: ['Written Exam', 'Technical Interview', 'HR Interview'],
        syllabus: ['C/C++', 'OS Concepts', 'DBMS', 'Computer Networks'],
        resources: {
          coding: 'https://prepinsta.com/wipro-coding-questions/',
          mcqs: 'https://www.indiabix.com/placement-papers/wipro/',
          pdf: 'https://www.prepinsta.com/wipro/placement-papers/',
        },
      },
      {
        id: 'accenture',
        name: 'Accenture',
        description: 'Multinational professional services company specializing in IT services.',
        interviewProcess: ['Cognitive & Technical Assessment', 'Interview'],
        syllabus: ['Aptitude', 'Coding', 'Communication'],
        resources: {
          coding: 'https://www.hackerrank.com/contests/accenture-coding/challenges',
          mcqs: 'https://www.indiabix.com/placement-papers/accenture/',
          pdf: 'https://prepinsta.com/how-to-prepare-for-accenture/#:~:text=This%20page%20will%20help%20you%20navigate%20with%20all,for%20the%20preparation%20of%20Accenture%20Campus%20placement%20drives.',
        },
      },
      {
        id: 'capgemini',
        name: 'Capgemini',
        description: 'French multinational IT services and consulting company.',
        interviewProcess: ['Written Test', 'Group Discussion', 'Interview'],
        syllabus: ['Quantitative Aptitude', 'Logical Reasoning', 'Pseudocodes'],
        resources: {
          coding: 'https://www.hackerrank.com/contests/capgemini-practice-test/challenges',
          mcqs: 'https://www.indiabix.com/placement-papers/capgemini/',
          pdf: 'https://www.freshersnow.com/capgemini-syllabus/',
        },
      },
      {
        id: 'cognizant',
        name: 'Cognizant',
        description: 'American multinational IT services and consulting company.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Interview'],
        syllabus: ['DSA', 'OOPs', 'DBMS', 'Basic Programming'],
        resources: {
          coding: 'https://www.hackerrank.com/contests/cognizant-company-specific-technical-practice-test-1/challenges',
          mcqs: 'https://www.indiabix.com/placement-papers/cognizant/',
          pdf: 'https://www.freshersnow.com/cognizant-placement-papers/',
        },
      },
      {
        id: 'hcl',
        name: 'HCL Technologies',
        description: 'Global IT services company focused on digital, IoT, and cloud solutions.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Interview'],
        syllabus: ['Quantitative Aptitude', 'Logical Reasoning', 'C/C++', 'DBMS'],
        resources: {
          coding: 'https://prepinsta.com/hcl/',
          mcqs: 'https://www.indiabix.com/placement-papers/hcl/',
          pdf: 'https://www.geeksforgeeks.org/hcl-sde-sheet-interview-questions-and-answers/',
        },
      },
      {
        id: 'techmahindra',
        name: 'Tech Mahindra',
        description: 'Indian IT services company specializing in telecom and digital solutions.',
        interviewProcess: ['Online Test', 'Technical Interview', 'HR Round'],
        syllabus: ['Aptitude', 'Coding', 'Networking', 'OS'],
        resources: {
          coding: 'https://prepinsta.com/tech-mahindra-placement-papers/coding/',
          mcqs: 'https://www.indiabix.com/placement-papers/tech-mahindra/',
          pdf: 'https://faceprep.in/article/tech-mahindra-recruitment-pattern-important-topics-and-about-the-company/',
        },
      },
    ],
    productBased: [
      {
        id: 'google',
        name: 'Google',
        description: 'Leading tech giant known for innovation in search, cloud, and AI.',
        interviewProcess: ['Online Coding Round', 'Technical Interviews (3-4)', 'Behavioral Interview'],
        syllabus: ['Algorithms', 'Data Structures', 'System Design', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/google/',
          mcqs: 'https://www.geeksforgeeks.org/google-interview-preparation/',
          pdf: 'https://bgibhopal.com/google-placement-process-a-step-by-step-guide-for-students/',
        },
      },
      {
        id: 'microsoft',
        name: 'Microsoft',
        description: 'Global leader in software, cloud computing, and AI solutions.',
        interviewProcess: ['Online Test', 'Technical Interviews (4-5)', 'HR Round'],
        syllabus: ['DSA', 'System Design', 'Coding', 'OOPs'],
        resources: {
          coding: 'https://leetcode.com/company/microsoft/',
          mcqs: 'https://www.geeksforgeeks.org/microsoft-interview-preparation/',
          pdf: 'https://prepinsta.com/microsoft/',
        },
      },
      {
        id: 'amazon',
        name: 'Amazon',
        description: 'E-commerce and cloud computing pioneer with a focus on innovation.',
        interviewProcess: ['Online Assessment', 'Technical Interviews (4-5)', 'Bar Raiser Round'],
        syllabus: ['DSA', 'System Design', 'Leadership Principles', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/amazon/',
          mcqs: 'https://www.geeksforgeeks.org/amazon-interview-preparation/',
          pdf: 'https://www.geeksforgeeks.org/how-to-prepare-for-amazon-software-development-engineering-interview/',
        },
      },
      {
        id: 'adobe',
        name: 'Adobe',
        description: 'Leader in creative software and digital experience solutions.',
        interviewProcess: ['Online Test', 'Technical Interviews (3-4)', 'HR Interview'],
        syllabus: ['DSA', 'Algorithms', 'OS', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/adobe/',
          mcqs: 'https://www.geeksforgeeks.org/adobe-interview-preparation/',
          pdf: 'https://www.freshersnow.com/adobe-placement-papers/',
        },
      },
      {
        id: 'facebook',
        name: 'Meta (Facebook)',
        description: 'Social media and tech innovator focused on connectivity and AI.',
        interviewProcess: ['Online Coding Round', 'Technical Interviews (3-4)', 'Behavioral Round'],
        syllabus: ['DSA', 'System Design', 'Algorithms', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/facebook/',
          mcqs: 'https://www.geeksforgeeks.org/facebook-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=facebook-interview-questions',
        },
      },
      {
        id: 'apple',
        name: 'Apple',
        description: 'Global leader in consumer electronics and software innovation.',
        interviewProcess: ['Online Test', 'Technical Interviews (4-5)', 'Team Fit Interview'],
        syllabus: ['DSA', 'System Design', 'OS', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/apple/',
          mcqs: 'https://www.geeksforgeeks.org/apple-interview-preparation/',
          pdf: 'https://www.geeksforgeeks.org/apple-sde-sheet-interview-questions-and-answers/',
        },
      },
      {
        id: 'netflix',
        name: 'Netflix',
        description: 'Streaming and entertainment giant with cutting-edge tech infrastructure.',
        interviewProcess: ['Online Coding', 'Technical Interviews (3-4)', 'Culture Fit Interview'],
        syllabus: ['DSA', 'System Design', 'Cloud Computing', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/netflix/',
          mcqs: 'https://www.geeksforgeeks.org/netflix-interview-preparation/',
          pdf: 'https://blog.internshala.com/how-to-get-a-job-in-netflix/',
        },
      },
    ],
  };

  // Find the selected company
  const allCompanies = [...companies.serviceBased, ...companies.productBased];
  const selectedCompany = allCompanies.find((company) => company.id === companyId);

  if (!selectedCompany) {
    return (
      <div className="min-h-screen bg-[#0A0F20] text-white font-sans">
        <div className="max-w-7xl mx-auto py-16 px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-100 mb-4">Company Not Found</h2>
          <p className="text-gray-300 mb-6">The selected company does not exist.</p>
          <Link
            to="/job-prep"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Job Prep
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F20] text-white font-sans">
      <div className="max-w-7xl mx-auto py-16 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[#1A1F30] p-8 rounded-xl mb-12 space-y-6 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-blue-400 mb-4">
            {selectedCompany.name} Placement Guide
          </h2>
          <p className="text-gray-300">{selectedCompany.description}</p>

          <section className="mb-6">
            <h3 className="text-xl font-semibold text-gray-200 mb-2">Interview Process</h3>
            <ul className="list-disc pl-6 space-y-1 text-gray-400">
              {selectedCompany.interviewProcess.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-semibold text-gray-200 mb-2">Syllabus</h3>
            <ul className="list-disc pl-6 space-y-1 text-gray-400">
              {selectedCompany.syllabus.map((topic, idx) => (
                <li key={idx}>{topic}</li>
              ))}
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-semibold text-gray-200 mb-2">Resources</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href={selectedCompany.resources.coding}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  🔹 Coding Practice Questions
                </a>
              </li>
              <li>
                <a
                  href={selectedCompany.resources.mcqs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  🔹 MCQs and Quizzes
                </a>
              </li>
              <li>
                <a
                  href={selectedCompany.resources.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  🔹 Preparation Guide
                </a>
              </li>
            </ul>
          </section>

          <Link
            to="/cp"
            className="inline-block mt-4 text-sm px-6 py-2 bg-gray-600 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Companies
          </Link>
        </motion.div>
      </div>

      {/* Floating Button */}
      <div className="fixed bottom-6 right-6">
        <button className="bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 transition">
          <FaLaptopCode className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default CompanyDetailsPage;